/**
 * Inline colour-swatch swap for product cards (PLP / carousels).
 *
 * Clicking a colour swatch fetches that product's card via the `?view=card`
 * template (templates/product.card.liquid, rendered with `layout none`) and
 * swaps it in place — image, title, price, quick-add and swatch row all update
 * without a page load. Works on desktop and touch.
 *
 * A swatch click NEVER navigates to the PDP (poor UX). If the fetch/swap fails
 * for any reason we fail silently and leave the current card in place.
 *
 * Delegated, so cards added later (AJAX filtering, the swap itself) work too.
 * The quick-shop and card-gallery scripts watch the DOM with their own
 * MutationObservers, so the swapped-in card auto-rebinds.
 */
(function () {
  var cache = {};

  // Fetch (and memoise) a colour's card HTML. Called on hover/touch to warm the
  // cache so the click feels instant, and on click.
  function load(url) {
    var fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'view=card';
    if (!cache[fetchUrl]) {
      cache[fetchUrl] = fetch(fetchUrl, { headers: { 'X-Requested-With': 'fetch' } })
        .then(function (r) {
          if (!r.ok) throw new Error('bad response');
          return r.text();
        })
        .catch(function (e) {
          delete cache[fetchUrl]; // allow a retry later
          throw e;
        });
    }
    return cache[fetchUrl];
  }

  function swapCard(wrapper, url) {
    // The swap template doesn't rebuild the swatch row (slow), so carry the
    // existing row over from the live card and just move the active state.
    var oldSwatches = wrapper.querySelector('.linked-color-swatches');

    wrapper.classList.add('is-swapping');

    load(url)
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('.card-wrapper');
        if (!fresh) throw new Error('no card in response');

        // Safari won't lazy-load images inserted via a DOM swap, leaving the
        // card half-rendered — force them to load.
        fresh.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
          img.setAttribute('loading', 'eager');
        });

        var freshSwatches = fresh.querySelector('.linked-color-swatches');
        if (oldSwatches && freshSwatches) {
          var row = oldSwatches.cloneNode(true);
          row.querySelectorAll('.linked-color-swatch').forEach(function (s) {
            var active = s.getAttribute('href') === url;
            s.classList.toggle('is-active', active);
            s.setAttribute('aria-checked', active ? 'true' : 'false');
          });
          // Keep the existing order — only move the active state, don't reorder.
          freshSwatches.replaceWith(row);
        }

        wrapper.replaceWith(fresh);
      })
      .catch(function () {
        // Never navigate to the PDP — fail silently, keep the current card.
        wrapper.classList.remove('is-swapping');
      });
  }

  // Warm the cache on hover/touch so the click is instant.
  function prefetchFromEvent(event) {
    var swatch = event.target.closest('.linked-color-swatch');
    if (!swatch || swatch.classList.contains('is-active')) return;
    var url = swatch.getAttribute('href');
    if (url) load(url).catch(function () {});
  }
  document.addEventListener('pointerover', prefetchFromEvent, { passive: true });
  document.addEventListener('touchstart', prefetchFromEvent, { passive: true });

  document.addEventListener(
    'click',
    function (event) {
      var swatch = event.target.closest('.linked-color-swatch');
      if (!swatch) return;

      // Only hijack swatches that live inside a product card (PLP / carousel).
      // PDP swatches use the same class but must navigate to the colour's PDP,
      // so if there's no card to swap, leave the link's default behaviour alone.
      var wrapper = swatch.closest('.card-wrapper');
      if (!wrapper) return;

      // Within a card, a swatch click never navigates — swap in place instead.
      event.preventDefault();
      event.stopPropagation();

      if (swatch.classList.contains('is-active')) return;

      var url = swatch.getAttribute('href');
      if (!url) return;

      swapCard(wrapper, url);
    },
    // Capture so we beat the card's own link handlers.
    true
  );
})();
